// client/src/components/pages/ProductMovement/ProductTreemapTile.tsx
//
// One tile of the "Product Volume Distribution" treemap (ST-00236). recharts clones this
// element once per node with the node's layout and data row as props.
//
// Label legibility, in both themes:
// - SVG `stroke` is an inherited property, and recharts puts the Treemap's own `stroke` prop
//   on the <svg>. Labels set `stroke="none"` so they never pick up an outline from an
//   ancestor (the "bold or outlined" names in ST-00236).
// - The tile fills come from a fixed series palette, so the label colour follows the fill,
//   not the theme: white or dark ink, whichever has the higher WCAG contrast on that fill.
// - A name that does not fit is shortened with an ellipsis, and a tile too small for a useful
//   label shows none. The <title> still names every tile on hover.
import { inkOn } from '../LexiStar/Replay/trips/tripsDay';
import { useThemeTokens } from '../../../utils/themeTokens';
import { formatTonnes } from './formatTonnes';

/** The node layout (plus the data row's fields) recharts' Treemap passes to `content`. */
export interface ProductTreemapTileProps {
  x: number;
  y: number;
  width: number;
  height: number;
  /** 0 = the root node that spans the whole chart, 1 = a product tile. */
  depth: number;
  name: string;
  tonnage: number;
  fill: string;
}

/** Horizontal padding inside a tile, each side, in px. */
const PAD_X = 6;
/** Average glyph width as a share of the font size: a conservative figure for the UI sans. */
const GLYPH_EM = 0.6;
/** Fewer characters than this (ellipsis included) says nothing, so the tile goes unlabelled. */
const MIN_LABEL_CHARS = 4;

/** Name font size for a tile of this width, in px. */
export const tileNameSize = (width: number): number => (width < 80 ? 10 : 12);

/**
 * The label that fits a tile `width` px wide at `fontSize`: the whole name, the name cut with
 * an ellipsis, or null when not even a few characters fit.
 */
export const fitTileLabel = (
  name: string | null | undefined,
  width: number,
  fontSize: number
): string | null => {
  if (!name) return null;
  const maxChars = Math.floor((width - 2 * PAD_X) / (fontSize * GLYPH_EM));
  if (name.length <= maxChars) return name;
  if (maxChars < MIN_LABEL_CHARS) return null;
  return `${name.slice(0, maxChars - 1).trimEnd()}…`;
};

const ProductTreemapTile = (props: Partial<ProductTreemapTileProps>) => {
  const { x = 0, y = 0, width = 0, height = 0, depth, name, tonnage, fill } = props;
  const t = useThemeTokens();
  // The root node spans the whole chart and has no fill of its own (an SVG rect without a
  // fill paints black), so only product tiles draw.
  if (depth === 0 || width <= 0 || height <= 0) return null;

  const ink = fill ? inkOn(fill) : t.textStrong;
  const nameSize = tileNameSize(width);
  const label = height >= nameSize + 10 ? fitTileLabel(name, width, nameSize) : null;
  const tonnageText = `${formatTonnes(tonnage)}t`;
  const showTonnage =
    label != null && height >= 44 && fitTileLabel(tonnageText, width, 10) === tonnageText;
  const cx = x + width / 2;
  const cy = y + height / 2;

  return (
    <g>
      <title>{name ? `${name}: ${tonnageText}` : tonnageText}</title>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke={t.surface}
        strokeWidth={2}
        rx={4}
      />
      {label != null && (
        <text
          x={cx}
          y={showTonnage ? cy - 7 : cy}
          textAnchor="middle"
          dominantBaseline="central"
          fill={ink}
          stroke="none"
          fontSize={nameSize}
          fontWeight={500}
          data-testid="treemap-tile-name"
        >
          {label}
        </text>
      )}
      {showTonnage && (
        <text
          x={cx}
          y={cy + 9}
          textAnchor="middle"
          dominantBaseline="central"
          fill={ink}
          fillOpacity={0.8}
          stroke="none"
          fontSize={10}
          data-testid="treemap-tile-tonnage"
        >
          {tonnageText}
        </text>
      )}
    </g>
  );
};

export default ProductTreemapTile;
